// =============================================================================
// PAGES/ADMINUSUARIOS.TSX - Gestão de usuários (SPEC 1, só ADMIN)
// =============================================================================
// Lista usuários do tenant M365 via Graph (User.ReadBasic.All) e cruza com
// GET /api/usuarios do backend. Admin libera acesso, define papel, ativa/desativa.
// Padrão idêntico ao central-reunioes/src/pages/AdminPage.jsx (AbaUsuarios).
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest, adminGraphRequest } from '../auth/authConfig';
import { listarUsuariosTenant, type UsuarioTenant } from '../services/graphService';
import type { UsuarioCRM, PapelUsuario } from '../types';
import api from '../services/api';
import { toast } from 'sonner';

// ── Linha da tabela (merge tenant + CRM) ─────────────────────────────────────

interface LinhaTenant {
  tenantId: string;
  displayName: string;
  email: string;
  accountEnabled: boolean;
  // dados do CRM (null = ainda não cadastrado)
  crmId: string | null;
  papel: PapelUsuario | null;
  ativo: boolean | null;
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function AdminUsuarios() {
  const { instance, accounts } = useMsal();
  const [linhas, setLinhas] = useState<LinhaTenant[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  async function carregar() {
    setCarregando(true);
    setErro('');
    try {
      // Token Graph (User.ReadBasic.All) - escopo separado do login
      const graphResult = await instance.acquireTokenSilent({
        ...adminGraphRequest,
        account: accounts[0],
      });
      const [tenantUsers, crmUsers] = await Promise.all([
        listarUsuariosTenant(graphResult.accessToken),
        api.get<UsuarioCRM[]>('/usuarios').then((r) => r.data),
      ]);

      const mapaCRM = new Map<string, UsuarioCRM>(crmUsers.map((u) => [u.email.toLowerCase(), u]));

      const merged: LinhaTenant[] = tenantUsers.map((u) => {
        const crm = mapaCRM.get(u.email.toLowerCase());
        return {
          tenantId: u.id,
          displayName: u.displayName,
          email: u.email,
          accountEnabled: u.accountEnabled,
          crmId: crm?.id ?? null,
          papel: crm?.papel ?? null,
          ativo: crm?.ativo ?? null,
        };
      });

      setLinhas(merged);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Falha ao carregar usuários.';
      setErro(msg);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Liberar acesso (POST)
  async function liberarAcesso(linha: LinhaTenant, papel: PapelUsuario) {
    setSalvando(linha.email);
    try {
      const { data } = await api.post<UsuarioCRM>('/usuarios', {
        email: linha.email,
        nome: linha.displayName,
        azureOid: linha.tenantId,
        papel,
      });
      setLinhas((prev) =>
        prev.map((l) =>
          l.email === linha.email
            ? { ...l, crmId: data.id, papel: data.papel, ativo: data.ativo }
            : l,
        ),
      );
      toast.success(`${linha.displayName} liberado como ${papel}.`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Erro ao liberar usuário.');
    } finally {
      setSalvando(null);
    }
  }

  // Alterar papel ou ativo (PATCH)
  async function atualizar(linha: LinhaTenant, patch: { papel?: PapelUsuario; ativo?: boolean }) {
    if (!linha.crmId) return;
    setSalvando(linha.email);
    try {
      const { data } = await api.patch<UsuarioCRM>(`/usuarios/${linha.crmId}`, patch);
      setLinhas((prev) =>
        prev.map((l) =>
          l.email === linha.email ? { ...l, papel: data.papel, ativo: data.ativo } : l,
        ),
      );
      toast.success('Atualizado.');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao atualizar.';
      toast.error(msg);
    } finally {
      setSalvando(null);
    }
  }

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return linhas;
    return linhas.filter(
      (l) => l.displayName.toLowerCase().includes(q) || l.email.includes(q),
    );
  }, [linhas, busca]);

  const totalLiberados = linhas.filter((l) => l.crmId !== null && l.ativo).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-slate-800 mb-1">Gestão de Usuários</h1>
      <p className="text-sm text-slate-500 mb-5">
        Lista o tenant M365 e controla quem acessa o CRM.{' '}
        <span className="font-medium text-blue-700">{totalLiberados} liberado(s)</span> de{' '}
        {linhas.length} pessoa(s).
      </p>

      {erro && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">
          {erro}
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome ou e-mail…"
          className="flex-1 max-w-sm border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={carregar}
          disabled={carregando}
          className="text-sm px-3 py-2 bg-otm-orange text-white rounded-lg hover:bg-otm-orange-dark disabled:opacity-50"
        >
          {carregando ? 'Carregando…' : 'Atualizar'}
        </button>
      </div>

      {carregando ? (
        <div className="text-center text-slate-400 py-12">Carregando usuários do tenant…</div>
      ) : filtradas.length === 0 ? (
        <div className="text-center text-slate-400 py-12">Nenhum usuário encontrado.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3">E-mail</th>
                <th className="text-left px-4 py-3 w-32">Acesso</th>
                <th className="text-left px-4 py-3 w-44">Papel</th>
                <th className="text-left px-4 py-3 w-28">Ação</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((linha) => (
                <LinhaUsuario
                  key={linha.email}
                  linha={linha}
                  salvando={salvando === linha.email}
                  onLiberar={liberarAcesso}
                  onAtualizar={atualizar}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Linha individual ──────────────────────────────────────────────────────────

interface LinhaProps {
  linha: LinhaTenant;
  salvando: boolean;
  onLiberar: (linha: LinhaTenant, papel: PapelUsuario) => void;
  onAtualizar: (linha: LinhaTenant, patch: { papel?: PapelUsuario; ativo?: boolean }) => void;
}

function LinhaUsuario({ linha, salvando, onLiberar, onAtualizar }: LinhaProps) {
  const liberado = linha.crmId !== null;
  const ativo = linha.ativo === true;

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3 font-medium text-slate-800">{linha.displayName}</td>
      <td className="px-4 py-3 text-slate-500">{linha.email}</td>

      {/* Coluna Acesso */}
      <td className="px-4 py-3">
        {liberado ? (
          <button
            disabled={salvando}
            onClick={() => onAtualizar(linha, { ativo: !ativo })}
            className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
              ativo
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {ativo ? 'Ativo' : 'Inativo'}
          </button>
        ) : (
          <span className="text-xs text-slate-400">-</span>
        )}
      </td>

      {/* Coluna Papel */}
      <td className="px-4 py-3">
        {liberado ? (
          <select
            disabled={salvando}
            value={linha.papel ?? 'ANALISTA'}
            onChange={(e) => onAtualizar(linha, { papel: e.target.value as PapelUsuario })}
            className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="ANALISTA">Analista</option>
            <option value="ATENDIMENTO">Atendimento</option>
            <option value="ADMIN">Admin</option>
          </select>
        ) : (
          <span className="text-xs text-slate-400">-</span>
        )}
      </td>

      {/* Coluna Ação */}
      <td className="px-4 py-3">
        {liberado ? (
          <span className="text-xs text-slate-400 italic">
            {salvando ? 'Salvando…' : 'Cadastrado'}
          </span>
        ) : (
          <button
            disabled={salvando}
            onClick={() => onLiberar(linha, 'ANALISTA')}
            className="text-xs px-3 py-1 bg-otm-orange text-white rounded-md hover:bg-otm-orange-dark disabled:opacity-50"
          >
            {salvando ? 'Liberando…' : 'Liberar'}
          </button>
        )}
      </td>
    </tr>
  );
}
